/**
 * Workshop version of FrameHeader — shared frame card header.
 *
 * Renders accent bar, icon, title, optional agent label, and status dot.
 * No React Flow or store dependencies.
 */

import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

interface FrameHeaderProps {
  title: string;
  icon: LucideIcon;
  accentColor?: string;
  agentName?: string;
  statusColor?: string;
  className?: string;
  "data-testid"?: string;
}

export function FrameHeader({
  title,
  icon: Icon,
  accentColor = "bg-accent",
  agentName,
  statusColor = "bg-muted",
  className,
  "data-testid": testId,
}: FrameHeaderProps) {
  return (
    <div
      className={cn(
        "group/header flex h-7 w-full items-center gap-1.5 border-b border-glass-stroke-faint pl-2 pr-1",
        className,
      )}
      data-testid={testId}
      data-frame-header="true"
    >
      {/* Accent bar */}
      <span
        className={cn("h-3 w-0.5 shrink-0 rounded", accentColor)}
        aria-hidden="true"
      />

      {/* Frame type icon */}
      <Icon className="h-2.5 w-2.5 shrink-0 text-muted" aria-hidden="true" />

      {/* Title */}
      <span className="flex-1 truncate text-canvas-sm font-semibold text-secondary">
        {title}
      </span>

      {/* Agent name */}
      {agentName && (
        <span
          className="inline-flex shrink-0 items-center"
          data-testid="frame-header-agent"
        >
          <span className="max-w-[60px] truncate text-canvas-2xs text-muted">
            {agentName}
          </span>
        </span>
      )}

      {/* Status dot */}
      <span
        className={cn("h-1.5 w-1.5 shrink-0 rounded-full", statusColor)}
        aria-hidden="true"
      />
    </div>
  );
}

export type { FrameHeaderProps };
